/**
 * Proactive refresh timer — D-02 belt of the belt-and-suspenders refresh strategy.
 *
 * Reads the current token's `exp` claim and schedules a setTimeout for
 * `exp - REFRESH_SKEW_SECONDS` seconds. When it fires, calls
 * `tryRefreshCrossTab` (fire-and-forget — any error is caught via the BC's
 * FAMILY_REVOKED flow or the next 401 retry).
 *
 * bfcache friendliness (Pitfall 5): on `visibilitychange === "hidden"` clear
 * the timer; on `visible` reschedule from the current token. A backgrounded
 * tab's stale timer would otherwise fire against an already-rotated refresh
 * cookie and trigger FAMILY_REVOKED falsely.
 *
 * Also cleared explicitly on logout (D-07 step 4).
 *
 * A refresh that got no verdict on the session (the server was unreachable or
 * answered 5xx) is retried on the same handle with a doubling delay, and
 * `isReconnecting()` reports the streak so the app can say it is trying.
 */
import { REFRESH_SKEW_SECONDS } from "../channel/broadcastSchema.js";
import { accessTokenStore } from "../tokens/accessTokenStore.js";
import { deliverToThisTab, tryRefreshCrossTab } from "./crossTabCoordinator.js";

let timerHandle: ReturnType<typeof setTimeout> | null = null;

function fire(): void {
  timerHandle = null;
  // Fire-and-forget: a refresh with no verdict reschedules itself (see
  // `retryRefreshLater`), and any other failure waits for the next 401.
  void tryRefreshCrossTab().catch((err: unknown) => {
    // A reused token revokes the whole family. performRefresh told the other
    // tabs on the channel, but a BroadcastChannel never delivers a tab's own
    // message, so this tab would sit on a dead session; on the api-client path
    // `onAuthFailure` covers it, here nothing would.
    const code = (err as { errorCode?: unknown } | null)?.errorCode;
    if (code === "AUTH_TOKEN_REUSED" || code === "REFRESH_FAMILY_REVOKED") {
      deliverToThisTab({ type: "FAMILY_REVOKED", reason: "reuse", correlationId: "(none)" });
    }
  });
}

export function scheduleProactiveRefresh(): void {
  clearProactiveRefresh();
  const record = accessTokenStore.getRecord();
  if (record === null) return;
  const nowSec = Math.floor(Date.now() / 1000);
  const delaySec = record.exp - REFRESH_SKEW_SECONDS - nowSec;
  // If the token is already inside the skew window (or expired), run the
  // refresh on the next tick — setTimeout with <=0 fires immediately.
  const delayMs = Math.max(0, delaySec * 1000);
  timerHandle = setTimeout(fire, delayMs);
}

// ─── retry after a refresh with no verdict ──────────────────────────────────

/** First wait after a refresh that got no answer or a 5xx; doubles to the cap. */
const RETRY_FIRST_MS = 2_000;
const RETRY_MAX_MS = 30_000;
let retryDelayMs = RETRY_FIRST_MS;
let reconnecting = false;
const reconnectingListeners = new Set<() => void>();

function setReconnecting(next: boolean): void {
  if (reconnecting === next) return;
  reconnecting = next;
  reconnectingListeners.forEach((l) => {
    l();
  });
}

/** True while a refresh is failing for want of a server and is being retried. */
export function isReconnecting(): boolean {
  return reconnecting;
}

/** `useSyncExternalStore`-shaped subscription to `isReconnecting`. */
export function subscribeReconnecting(listener: () => void): () => void {
  reconnectingListeners.add(listener);
  return () => {
    reconnectingListeners.delete(listener);
  };
}

/**
 * A refresh got no verdict on the session (no answer, or a 5xx): keep the
 * session and try again on a doubling delay, on the proactive timer's own
 * handle, so logout and a hidden tab cancel it like any proactive refresh.
 * Only the coordinator calls this, and only for those two failures, so a
 * refused session (401, a revoked family) is never retried.
 */
export function retryRefreshLater(): void {
  // Concurrent callers share one failed refresh: one retry is enough, and
  // each of them doubling the delay would jump straight to the cap.
  if (reconnecting && timerHandle !== null) return;
  setReconnecting(true);
  clearProactiveRefresh();
  // Up to 30% jitter, so the tabs and browsers that lost the server together
  // don't all come back at the same instant.
  const delayMs = retryDelayMs * (1 + Math.random() * 0.3);
  retryDelayMs = Math.min(retryDelayMs * 2, RETRY_MAX_MS);
  timerHandle = setTimeout(fire, delayMs);
}

/**
 * The server answered a refresh, here or in another tab: a new token, or a
 * refusal. Either way it is reachable, so the notice goes and the next streak
 * of failures starts from the first delay.
 */
export function refreshSettled(): void {
  retryDelayMs = RETRY_FIRST_MS;
  setReconnecting(false);
}

export function clearProactiveRefresh(): void {
  if (timerHandle !== null) {
    clearTimeout(timerHandle);
    timerHandle = null;
  }
}

/**
 * Wire `document.visibilitychange` so a backgrounded tab releases its timer
 * and a foregrounded tab reschedules from the current token.
 *
 * Returns an unsubscribe handle.
 */
export function installBfcacheHandler(): () => void {
  if (typeof document === "undefined") {
    return () => {};
  }
  const handler = (): void => {
    if (document.visibilityState === "hidden") {
      clearProactiveRefresh();
    } else if (document.visibilityState === "visible") {
      scheduleProactiveRefresh();
    }
  };
  document.addEventListener("visibilitychange", handler);
  return () => {
    document.removeEventListener("visibilitychange", handler);
  };
}
